import { get, set, del, keys } from 'idb-keyval';
import type { Menu } from '../../shared/menu';

interface StoredMenu {
  menu: Menu;
  modelUsed: string;
  savedAt: number;
}

const PREFIX = 'menu:';

export async function saveMenuLocal(menuId: string, menu: Menu, modelUsed: string): Promise<void> {
  await set(PREFIX + menuId, { menu, modelUsed, savedAt: Date.now() } satisfies StoredMenu);
}

export async function loadMenuLocal(menuId: string): Promise<StoredMenu | null> {
  const raw = await get<StoredMenu>(PREFIX + menuId);
  return raw ?? null;
}

export async function listLocalMenus(): Promise<string[]> {
  const allKeys = await keys();
  return allKeys
    .filter((k): k is string => typeof k === 'string' && k.startsWith(PREFIX))
    .map((k) => k.slice(PREFIX.length));
}

export interface LocalMenuSummary {
  menuId: string;
  restaurantName: string | null;
  sourceLanguage: string;
  targetLanguage: string;
  savedAt: string; // ISO so it matches the server's SavedMenuListItem shape
  // First pageImage if any — surfaced as the menu-card thumbnail. Either a
  // /api/menu/:id/page/0 proxy URL or a data: URI in dev. Null when the
  // parse never produced page bytes (rare; legacy or attach-failed cases).
  thumbnailRef: string | null;
}

export async function listLocalMenusWithMetadata(): Promise<LocalMenuSummary[]> {
  const ids = await listLocalMenus();
  const rows = await Promise.all(
    ids.map(async (menuId) => {
      const stored = await loadMenuLocal(menuId);
      if (!stored) return null;
      return {
        menuId,
        restaurantName: stored.menu.restaurantName ?? null,
        sourceLanguage: stored.menu.detectedSourceLanguage,
        targetLanguage: stored.menu.targetLanguage,
        savedAt: new Date(stored.savedAt).toISOString(),
        thumbnailRef: stored.menu.pageImages?.[0] ?? null,
      } satisfies LocalMenuSummary;
    }),
  );
  return rows
    .filter((r): r is LocalMenuSummary => r !== null)
    .sort((a, b) => (a.savedAt < b.savedAt ? 1 : -1));
}

export async function removeMenuLocal(menuId: string): Promise<void> {
  await del(PREFIX + menuId);
}
